import { useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTomarFotoZoom, useZoomConfig, type Obj } from "@/hooks/useZoom";

// Pestaña "Configuración" de la consola Zoom: la fotografía diaria de cómo está
// armado Zoom (services/zoom_config.py) y qué cambió contra la anterior.
// Sólo lectura: el botón toma una foto, no cambia nada en Zoom.

const o = (v: unknown): Obj => (v && typeof v === "object" ? (v as Obj) : {});
const lista = (v: unknown): Obj[] => (Array.isArray(v) ? (v as Obj[]) : []);
const txt = (v: unknown): string =>
  v === null || v === undefined
    ? "—"
    : typeof v === "object"
      ? JSON.stringify(v)
      : String(v);

const fecha = (iso: string) =>
  new Date(iso).toLocaleString("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  });

// Del bloque de horario de Zoom, lo que una persona quiere leer.
function manejo(m: unknown) {
  const mm = o(m);
  const laboral = lista(mm.business_hours);
  const llamada = o(
    laboral.find((x) => x.sub_setting_type === "call_handling")?.settings,
  );
  const horario = o(
    laboral.find((x) => x.sub_setting_type === "custom_hours")?.settings,
  );
  // `action` 1 = buzón de voz del destino (visto en CQ_Nova); 7 = pasa la
  // llamada al destino (visto en el auto-receptionist 801). Otro código se
  // muestra tal cual en vez de adivinarlo.
  const destino = (bloque: unknown) => {
    const r = o(o(lista(bloque)[0]?.settings).routing);
    const f = o(r.forward_to);
    const quien = f.display_name
      ? `${txt(f.display_name)} (${txt(f.extension_number)})`
      : "";
    if (r.action === 1) return `buzón de voz ${quien}`.trim();
    if (r.action === 7) return `pasa a ${quien}`.trim();
    return r.action !== undefined
      ? `acción ${txt(r.action)} ${quien}`.trim()
      : "—";
  };
  return {
    horario:
      horario.type === 1
        ? "24 horas"
        : horario.type === 2
          ? "Horario personalizado"
          : "—",
    esperaMax: llamada.max_wait_time,
    maxEnCola: llamada.max_call_in_queue,
    timbrado: o(llamada.call_distribution).ring_mode,
    saludo: o(llamada.greeting_prompt).name,
    desborde: destino(
      laboral.filter((x) => x.sub_setting_type === "call_handling"),
    ),
    fueraDeHorario: destino(mm.closed_hours),
    festivos: lista(mm.holiday_hours).length
      ? destino(mm.holiday_hours)
      : "Sin festivos",
  };
}

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {titulo}
      </h3>
      {children}
    </div>
  );
}

export function ZoomConfigTab() {
  const [fotoId, setFotoId] = useState<number | undefined>();
  const q = useZoomConfig(fotoId);
  const tomar = useTomarFotoZoom();

  if (q.isPending)
    return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (q.isError)
    return (
      <p className="text-sm text-red-600">No se pudo leer: {String(q.error)}</p>
    );
  const { foto, historial } = q.data;

  const boton = (
    <Button
      size="sm"
      disabled={tomar.isPending}
      onClick={() => {
        setFotoId(undefined);
        tomar.mutate();
      }}
    >
      {tomar.isPending ? "Leyendo Zoom…" : "Tomar foto ahora"}
    </Button>
  );
  if (!foto)
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Todavía no hay fotos. La primera se toma sola en la madrugada.
        </p>
        {boton}
      </div>
    );

  const c = foto.contenido;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">
          Foto del <b>{fecha(foto.tomada_en)}</b> ·{" "}
          {foto.tomada_por === "aroc-auto" ? "automática" : foto.tomada_por}
        </p>
        {boton}
      </div>
      {tomar.isError && (
        <p className="text-sm text-red-600">{String(tomar.error)}</p>
      )}
      {foto.errores.length > 0 && (
        <p className="text-sm text-amber-600">
          No se pudo leer: {foto.errores.join(" · ")}
        </p>
      )}

      <Seccion
        titulo={`Cambios contra la foto anterior (${foto.cambios.length})`}
      >
        {foto.cambios.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin cambios.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Qué</TableHead>
                <TableHead>Antes</TableHead>
                <TableHead>Ahora</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {foto.cambios.map((x) => (
                <TableRow key={x.ruta}>
                  <TableCell className="text-xs">{x.ruta}</TableCell>
                  <TableCell className="text-xs text-red-600">
                    {txt(x.antes)}
                  </TableCell>
                  <TableCell className="text-xs text-emerald-700">
                    {txt(x.despues)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Seccion>

      <Seccion titulo="Licencias">
        <div className="flex flex-wrap gap-3">
          {Object.entries(c.planes ?? {}).map(([plan, p]) => (
            <Card key={plan} className="min-w-56">
              <CardContent className="pt-4 text-sm">
                <div className="font-medium">{plan}</div>
                <div className="tabular-nums">
                  {txt(p.asignadas)} asignadas de {txt(p.contratadas)} ·{" "}
                  <b
                    className={
                      Number(p.disponibles) > 0 ? "text-amber-600" : ""
                    }
                  >
                    {txt(p.disponibles)} sin usar
                  </b>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Seccion>

      <Seccion titulo="Números (DID) y a dónde apuntan">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Número</TableHead>
              <TableHead>Apunta a</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(c.numeros ?? {}).map(([num, n]) => (
              <TableRow key={num}>
                <TableCell className="tabular-nums">{num}</TableCell>
                <TableCell>
                  {txt(n.apunta_a)}{" "}
                  {n.extension ? (
                    <span className="text-muted-foreground">
                      ({txt(n.extension)})
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {txt(n.tipo_destino)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {txt(n.estado)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Seccion>

      <Seccion titulo="Recepcionistas automáticas">
        {Object.entries(c.recepcionistas ?? {}).map(([nombre, r]) => {
          const m = manejo(r.manejo);
          return (
            <p key={nombre} className="text-sm">
              <b>{nombre}</b> · atiende{" "}
              {lista(r.numeros).length
                ? (r.numeros as string[]).join(", ")
                : "—"}{" "}
              · manda a <b>{m.desborde}</b> · fuera de horario:{" "}
              {m.fueraDeHorario}
              {r.ivr ? " · tiene menú de voz" : " · sin menú de voz"}
            </p>
          );
        })}
      </Seccion>

      <Seccion titulo="Colas">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Object.entries(c.colas ?? {}).map(([nombre, cola]) => {
            const m = manejo(cola.manejo);
            const miembros = Object.entries(o(cola.miembros));
            return (
              <Card key={nombre}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{nombre}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Miembros: </span>
                    {miembros.length === 0 ? (
                      <Badge variant="destructive">ninguno</Badge>
                    ) : (
                      miembros.map(([mn, mv]) => (
                        <Badge
                          key={mn}
                          variant={
                            o(mv).recibe_llamadas ? "secondary" : "outline"
                          }
                          className="mr-1"
                        >
                          {mn}
                          {o(mv).recibe_llamadas ? "" : " (no recibe)"}
                        </Badge>
                      ))
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Horario: </span>
                    {m.horario}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Espera máx.: </span>
                    {txt(m.esperaMax)} s ·{" "}
                    <span className="text-muted-foreground">
                      máx. en cola:{" "}
                    </span>
                    {txt(m.maxEnCola)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Timbrado: </span>
                    {txt(m.timbrado)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Saludo: </span>
                    {txt(m.saludo)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Si nadie contesta:{" "}
                    </span>
                    {m.desborde}
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Fuera de horario:{" "}
                    </span>
                    {m.fueraDeHorario}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Festivos: </span>
                    {m.festivos}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </Seccion>

      <Seccion titulo="Prefijos de marcación (reglas de ruteo)">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Patrón</TableHead>
              <TableHead>Sale por</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(c.reglas_ruteo ?? {})
              .sort(([, a], [, b]) => Number(a.orden) - Number(b.orden))
              .map(([nombre, r]) => (
                <TableRow key={nombre}>
                  <TableCell className="tabular-nums">{txt(r.orden)}</TableCell>
                  <TableCell>{nombre}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {txt(r.patron)}
                  </TableCell>
                  <TableCell>{txt(r.destino)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Seccion>

      <Seccion titulo="Historial de fotos">
        <div className="flex flex-wrap gap-2">
          {historial.map((h) => (
            <Button
              key={h.id}
              size="sm"
              variant={h.id === foto.id ? "default" : "outline"}
              onClick={() => setFotoId(h.id)}
              title={h.tomada_por}
            >
              {new Date(h.tomada_en).toLocaleDateString("es-MX", {
                day: "2-digit",
                month: "short",
              })}
              {h.cambios > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {h.cambios}
                </Badge>
              )}
            </Button>
          ))}
        </div>
      </Seccion>
    </div>
  );
}
